import React, { useMemo } from 'react';
import styles from './StudentsInfo.module.scss';
import Text from '../../atoms/text/Text';
import HtmlTag from '../htmlTag/HtmlTag';
import { useMediaQuery } from 'react-responsive';
const closeSvg = '/icons/close.svg';

const stripTags = (html = '') =>
  String(html)
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const estimateLines = (textLen, { isMobile, isTablet }) => {
  const CPL = isMobile ? 32 : isTablet ? 48 : 64;
  return Math.ceil(textLen / CPL);
};

const StudentsInfo = ({
  title,
  description,
  descriptionClass,
  doubleQuoteLeft,
  doubleQuoteRight,
  containerClass,
  footerText,
  date,
  isTestimonial,
  showData = true,
  onReadMoreClick,
  color,
  hasClose,
  onClose,
  footerClass,
  limited,
  isRtl,
  limitHeight,
  isModal,
  isFull,
  removeBorder = false,
}) => {
  const containerClasses = `${styles['students-info']} ${containerClass || ''}`;
  const isTablet = useMediaQuery({ maxWidth: 1200 });
  const isMobile = useMediaQuery({ maxWidth: 600 });

  const maxLines = isFull
    ? 100000
    : isModal
      ? 100
      : isMobile
        ? 5
        : isTablet
          ? 10
          : limited
            ? 6
            : null;

  const shouldShowReadMore = useMemo(() => {
    if (!onReadMoreClick) return false;
    if (isFull) return false;
    if (maxLines == null) return false;
    const text = stripTags(description);
    if (!text) return false;
    const lines = estimateLines(text.length, { isMobile, isTablet });
    return lines > maxLines;
  }, [onReadMoreClick, isFull, maxLines, description, isMobile, isTablet]);

  return (
    <div
      className={`${containerClasses} ${isTestimonial ? styles['testimonial'] : ''}`}
      style={{ direction: isRtl ? 'rtl' : 'ltr' }}
    >
      <img
        className={styles['left-quotes']}
        src={doubleQuoteLeft}
        alt="Left Quote"
      />
      <img
        className={styles['right-quotes']}
        src={doubleQuoteRight}
        alt="Right Quote"
      />
      <div className={styles['right-border']}></div>
      {!removeBorder && <div className={styles['left-border']}></div>}

      <div className={styles['header-container']}>
        <div>
          <HtmlTag
            title={title}
            variant="text-bold-italic-20"
            style={{ textTransform: 'uppercase' }}
            color={color}
          />
          {!isTestimonial && (
            <Text title={footerText} variant="text-regular-16" color={color} />
          )}
        </div>
        {hasClose ? (
          <img src={closeSvg} className={styles.closeSvg} onClick={onClose} />
        ) : null}
      </div>

      <div className={styles['desc-wrapper']}>
        <div
          className={`${styles['description']} ${descriptionClass ?? ''}`}
          style={{ maxHeight: isFull ? 'max-content' : '' }}
        >
          <HtmlTag
            title={description}
            variant="text-regular-18"
            externalLineHeight={isTablet ? 1.2 : 1.5}
            maxLines={maxLines}
            color={color}
            className={styles['description-text']}
          />
        </div>

        {showData && isTestimonial && (
          <div className={`${styles['footer']} ${footerClass ?? ''}`}>
            <HtmlTag
              title={footerText}
              variant="text-regular-16"
              color={color}
            />
            {isTestimonial && <Text title={date} variant="text-regular-14" />}
            {!isTestimonial && <br />}
          </div>
        )}

        {shouldShowReadMore ? (
          <div className={styles['read-more']} dir={isRtl ? 'ltr' : ''}>
            <Text
              title="home.read_more"
              variant="text-regular-16"
              onClick={() => onReadMoreClick()}
              className={styles.text}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default StudentsInfo;
